import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { RiskLevel } from '../../../types/checkin';
import { Button } from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  MapPinIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface CrisisOverlayProps {
  riskLevel: RiskLevel;
  isOpen: boolean;
  onClose: () => void;
}

interface HelplineInfo {
  name: string;
  number: string;
  description: string;
  icon: typeof PhoneIcon;
}

const helplines: HelplineInfo[] = [
  {
    name: 'SAMHSA National Helpline',
    number: '1-800-662-4357',
    description: '24/7 treatment referral and information',
    icon: PhoneIcon,
  },
  {
    name: '988 Suicide & Crisis Lifeline',
    number: '988',
    description: '24/7 crisis support and suicide prevention',
    icon: PhoneIcon,
  },
  {
    name: 'Crisis Text Line',
    number: '741741',
    description: 'Text HOME to 741741 for 24/7 crisis support',
    icon: ChatBubbleLeftRightIcon,
  },
];

export function CrisisOverlay({ riskLevel, isOpen, onClose }: CrisisOverlayProps) {
  const navigate = useNavigate();

  // Crisis overlay should only show for HIGH or CRISIS levels
  if (riskLevel !== 'high' && riskLevel !== 'crisis') {
    return null;
  }

  const isCrisisLevel = riskLevel === 'crisis';

  // CRISIS level cannot be dismissed easily
  const canDismiss = !isCrisisLevel;

  const handleContactSponsor = () => {
    // TODO: Navigate to sponsor contact page when implemented
    // For now, just close the overlay
    navigate('/dashboard');
    onClose();
  };

  const handleFindMeeting = () => {
    navigate('/meetings');
    onClose();
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const overlayVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: { duration: 0.2 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={canDismiss ? onClose : undefined}
          />

          {/* Overlay Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto"
            >
              <Card variant="elevated" className="relative">
                {/* Close Button (only for HIGH risk) */}
                {canDismiss && (
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-5 h-5 text-surface-500 dark:text-surface-400" />
                  </button>
                )}

                <CardContent className="space-y-6 pt-6">
                  {/* Header */}
                  <div className="text-center">
                    <div className="text-6xl mb-4 animate-pulse">
                      {isCrisisLevel ? '🆘' : '⚠️'}
                    </div>
                    <h2 className={`text-3xl font-bold mb-2 ${
                      isCrisisLevel
                        ? 'text-danger-600 dark:text-danger-400'
                        : 'text-warning-600 dark:text-warning-400'
                    }`}>
                      {isCrisisLevel ? 'Crisis Support Needed' : 'High Risk Detected'}
                    </h2>
                    <p className="text-lg text-surface-700 dark:text-surface-300">
                      {isCrisisLevel
                        ? 'Your safety is our priority. Please reach out for immediate support.'
                        : 'We\'re concerned about your well-being. Consider connecting with support.'}
                    </p>
                  </div>

                  {/* Emergency Helplines */}
                  <div>
                    <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-4">
                      24/7 Crisis Helplines
                    </h3>
                    <div className="space-y-3">
                      {helplines.map((helpline) => {
                        const Icon = helpline.icon;
                        const isTextLine = helpline.number === '741741';

                        return (
                          <a
                            key={helpline.name}
                            href={isTextLine ? `sms:${helpline.number}` : `tel:${helpline.number}`}
                            className="block"
                          >
                            <Card
                              variant="outlined"
                              hoverable
                              className={`transition-colors min-h-[44px] ${
                                isCrisisLevel
                                  ? 'hover:border-danger-500 dark:hover:border-danger-400'
                                  : 'hover:border-warning-500 dark:hover:border-warning-400'
                              }`}
                            >
                              <CardContent className="!p-4">
                                <div className="flex items-start gap-4">
                                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                                    isCrisisLevel
                                      ? 'bg-danger-100 dark:bg-danger-900/30'
                                      : 'bg-warning-100 dark:bg-warning-900/30'
                                  }`}>
                                    <Icon className={`w-6 h-6 ${
                                      isCrisisLevel
                                        ? 'text-danger-600 dark:text-danger-400'
                                        : 'text-warning-600 dark:text-warning-400'
                                    }`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-surface-900 dark:text-surface-100">
                                      {helpline.name}
                                    </h4>
                                    <p className={`text-lg font-bold ${
                                      isCrisisLevel
                                        ? 'text-danger-600 dark:text-danger-400'
                                        : 'text-warning-600 dark:text-warning-400'
                                    }`}>
                                      {helpline.number}
                                    </p>
                                    <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                                      {helpline.description}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </a>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recovery Actions */}
                  <div>
                    <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-4">
                      Recovery Support
                    </h3>
                    <div className="space-y-3">
                      <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={handleContactSponsor}
                        leftIcon={<UserGroupIcon className="w-5 h-5" />}
                      >
                        Contact My Sponsor
                      </Button>
                      <Button
                        variant="accent"
                        size="lg"
                        fullWidth
                        onClick={handleFindMeeting}
                        leftIcon={<MapPinIcon className="w-5 h-5" />}
                      >
                        Find a Meeting Near Me
                      </Button>
                    </div>
                  </div>

                  {/* Dismissal Message for CRISIS level */}
                  {isCrisisLevel && (
                    <div className="text-center pt-4 border-t border-surface-200 dark:border-surface-700">
                      <p className="text-sm text-surface-600 dark:text-surface-400">
                        Please use one of the resources above before continuing.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export default CrisisOverlay;
